import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  CalendarClock,
  Plus,
  Pencil,
  Trash2,
  Users,
  Search,
  XCircle,
  Loader2,
  CheckCircle2,
  MapPin,
  Tag,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAdminRole } from "../hooks/useAdminRole.ts";
import socioService from "../services/socioService.tsx";
import { Socio } from "../types/Socio.tsx";
import type { EstadoEvento, EventoSocio } from "../types/Evento.tsx";
import {
  listarAcontecimientos,
  crearAcontecimiento,
  actualizarAcontecimiento,
  eliminarAcontecimiento,
  listarUbicaciones,
  listarCategorias,
  listarSociosDeAcontecimiento,
  asignarSociosAcontecimiento,
  desasignarSocioAcontecimiento,
  AcontecimientoAdmin,
  AcontecimientoPayload,
  UbicacionEvento,
  CategoriaEvento,
} from "../services/eventosAdminService.tsx";

const ESTADOS: { value: EstadoEvento; label: string }[] = [
  { value: "borrador", label: "Borrador" },
  { value: "publicado", label: "Publicado" },
  { value: "en_curso", label: "En curso" },
  { value: "finalizado", label: "Finalizado" },
  { value: "cancelado", label: "Cancelado" },
];

const estadoBadge = (estado: string) => {
  switch (estado) {
    case "publicado":
      return "bg-emerald-100 text-emerald-700";
    case "en_curso":
      return "bg-blue-100 text-blue-700";
    case "finalizado":
      return "bg-slate-100 text-slate-600";
    case "cancelado":
      return "bg-rose-100 text-rose-700";
    case "borrador":
    default:
      return "bg-amber-100 text-amber-700";
  }
};

const estadoLabel = (estado: string) =>
  ESTADOS.find((e) => e.value === estado)?.label || estado;

const pad = (n: number) => String(n).padStart(2, "0");

const toLocalInput = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const formatFecha = (value?: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

type FormState = {
  nombre: string;
  descripcion: string;
  estado: EstadoEvento;
  fecha: string;
  fecha_fin: string;
  cupo: string;
  imagen_url: string;
  ubicacion: string;
  categoria: string;
};

const formVacio: FormState = {
  nombre: "",
  descripcion: "",
  estado: "borrador",
  fecha: "",
  fecha_fin: "",
  cupo: "",
  imagen_url: "",
  ubicacion: "",
  categoria: "",
};

export default function EventosGestor() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAdminRole();

  const [eventos, setEventos] = useState<AcontecimientoAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [filtroEstado, setFiltroEstado] = useState<string>("");
  const [ubicaciones, setUbicaciones] = useState<UbicacionEvento[]>([]);
  const [categorias, setCategorias] = useState<CategoriaEvento[]>([]);
  const [msg, setMsg] = useState<{ tipo: "ok" | "error"; texto: string } | null>(null);

  // Modal crear/editar
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(formVacio);
  const [guardando, setGuardando] = useState(false);

  // Modal socios
  const [selectedEvento, setSelectedEvento] = useState<AcontecimientoAdmin | null>(null);
  const [sociosEnEvento, setSociosEnEvento] = useState<EventoSocio[]>([]);
  const [loadingSocios, setLoadingSocios] = useState(false);
  const [showAddSocios, setShowAddSocios] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Socio[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedSocios, setSelectedSocios] = useState<number[]>([]);
  const [asignando, setAsignando] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/eventos");
      return;
    }
    loadEventos();
    loadCatalogos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const loadEventos = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const data = await listarAcontecimientos(undefined, token);
      setEventos(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al cargar eventos:", e);
      setMsg({ tipo: "error", texto: "No se pudieron cargar los eventos" });
    } finally {
      setLoading(false);
    }
  };

  const loadCatalogos = async () => {
    try {
      const token = await getAccessTokenSilently();
      const [ub, cat] = await Promise.all([
        listarUbicaciones(token).catch(() => []),
        listarCategorias(token).catch(() => []),
      ]);
      setUbicaciones(Array.isArray(ub) ? ub : []);
      setCategorias(Array.isArray(cat) ? cat : []);
    } catch (e) {
      console.error("Error al cargar catálogos:", e);
    }
  };

  const abrirNuevo = () => {
    setEditingId(null);
    setForm({ ...formVacio });
    setMsg(null);
    setShowForm(true);
  };

  const abrirEditar = (ev: AcontecimientoAdmin) => {
    setEditingId(ev.acontecimiento_id);
    setForm({
      nombre: ev.nombre || "",
      descripcion: ev.descripcion || "",
      estado: (ev.estado as EstadoEvento) || "borrador",
      fecha: toLocalInput(ev.fecha),
      fecha_fin: toLocalInput(ev.fecha_fin),
      cupo: ev.cupo != null ? String(ev.cupo) : "",
      imagen_url: ev.imagen_url || "",
      ubicacion: ev.ubicacion?.nombre || "",
      categoria: ev.categoria?.nombre || "",
    });
    setMsg(null);
    setShowForm(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) {
      setMsg({ tipo: "error", texto: "El título es obligatorio" });
      return;
    }
    if (!form.fecha) {
      setMsg({ tipo: "error", texto: "La fecha de inicio es obligatoria" });
      return;
    }
    if (!form.ubicacion.trim()) {
      setMsg({ tipo: "error", texto: "La ubicación es obligatoria" });
      return;
    }
    if (form.fecha_fin && new Date(form.fecha_fin) < new Date(form.fecha)) {
      setMsg({ tipo: "error", texto: "La fecha de fin no puede ser anterior a la de inicio" });
      return;
    }

    const payload: AcontecimientoPayload = {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion.trim() || null,
      fecha: form.fecha,
      fecha_fin: form.fecha_fin || null,
      estado: form.estado,
      cupo: form.cupo === "" ? null : Number(form.cupo),
      imagen_url: form.imagen_url.trim() || null,
      ubicacion_nombre: form.ubicacion.trim(),
      categoria_nombre: form.categoria.trim() || null,
    };

    setGuardando(true);
    try {
      const token = await getAccessTokenSilently();
      if (editingId) {
        await actualizarAcontecimiento(editingId, payload, token);
        setMsg({ tipo: "ok", texto: "Evento actualizado" });
      } else {
        await crearAcontecimiento(payload, token);
        setMsg({ tipo: "ok", texto: "Evento creado" });
      }
      setShowForm(false);
      await loadEventos();
      await loadCatalogos();
    } catch (e: any) {
      const detalle = e?.response?.data?.message || "No se pudo guardar el evento";
      setMsg({ tipo: "error", texto: detalle });
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (ev: AcontecimientoAdmin) => {
    if (!window.confirm(`¿Eliminar el evento "${ev.nombre}"?`)) return;
    try {
      const token = await getAccessTokenSilently();
      await eliminarAcontecimiento(ev.acontecimiento_id, token);
      setMsg({ tipo: "ok", texto: "Evento eliminado" });
      await loadEventos();
    } catch (e) {
      setMsg({ tipo: "error", texto: "Error al eliminar el evento" });
    }
  };

  const abrirSocios = async (ev: AcontecimientoAdmin) => {
    setSelectedEvento(ev);
    setShowAddSocios(false);
    setSelectedSocios([]);
    setSearchResults([]);
    setSearchTerm("");
    setLoadingSocios(true);
    try {
      const token = await getAccessTokenSilently();
      const socios = await listarSociosDeAcontecimiento(ev.acontecimiento_id, token);
      setSociosEnEvento(Array.isArray(socios) ? socios : []);
    } catch (e) {
      console.error("Error al cargar socios del evento:", e);
    } finally {
      setLoadingSocios(false);
    }
  };

  const handleSearchSocios = async (term: string) => {
    setSearchTerm(term);
    if (term.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const results = await socioService.searchSocios(term, getAccessTokenSilently);
      const yaEnEvento = sociosEnEvento.map((s) => Number(s.socio_id));
      setSearchResults(
        (Array.isArray(results) ? results : []).filter(
          (s: Socio) => !yaEnEvento.includes(Number(s.socio_id))
        )
      );
    } catch (e) {
      console.error("Error al buscar socios:", e);
    } finally {
      setSearching(false);
    }
  };

  const handleToggleSocio = (socioId: number) => {
    setSelectedSocios((prev) =>
      prev.includes(socioId) ? prev.filter((id) => id !== socioId) : [...prev, socioId]
    );
  };

  const handleAsignar = async () => {
    if (!selectedEvento || selectedSocios.length === 0) return;
    setAsignando(true);
    try {
      const token = await getAccessTokenSilently();
      await asignarSociosAcontecimiento(
        selectedEvento.acontecimiento_id,
        selectedSocios,
        token
      );
      setMsg({ tipo: "ok", texto: `${selectedSocios.length} socio(s) asignados` });
      setShowAddSocios(false);
      setSelectedSocios([]);
      setSearchTerm("");
      setSearchResults([]);
      await abrirSocios(selectedEvento);
      await loadEventos();
    } catch (e) {
      setMsg({ tipo: "error", texto: "Error al asignar socios" });
    } finally {
      setAsignando(false);
    }
  };

  const handleQuitarSocio = async (socioId: number) => {
    if (!selectedEvento) return;
    if (!window.confirm("¿Quitar este socio del evento?")) return;
    try {
      const token = await getAccessTokenSilently();
      await desasignarSocioAcontecimiento(selectedEvento.acontecimiento_id, socioId, token);
      setSociosEnEvento((prev) =>
        prev.filter((s) => Number(s.socio_id) !== Number(socioId))
      );
      setMsg({ tipo: "ok", texto: "Socio quitado del evento" });
      await loadEventos();
    } catch (e) {
      setMsg({ tipo: "error", texto: "Error al quitar socio" });
    }
  };

  const eventosFiltrados = useMemo(
    () =>
      eventos.filter((e) => {
        const matchNombre = e.nombre.toLowerCase().includes(filtro.toLowerCase());
        const matchEstado = !filtroEstado || e.estado === filtroEstado;
        return matchNombre && matchEstado;
      }),
    [eventos, filtro, filtroEstado]
  );

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Gestión de Eventos</h1>
            <p className="mt-1 text-sm text-slate-500">
              Creá, editá y asigná socios a los eventos de la cámara
            </p>
          </div>
          <button
            onClick={abrirNuevo}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" /> Nuevo evento
          </button>
        </div>

        {msg && (
          <div
            className={`mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${
              msg.tipo === "ok"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-rose-50 text-rose-700 border border-rose-200"
            }`}
          >
            {msg.tipo === "ok" ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            {msg.texto}
          </div>
        )}

        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por título..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="">Todos los estados</option>
            {ESTADOS.map((e) => (
              <option key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : eventosFiltrados.length === 0 ? (
          <div className="rounded-2xl border border-slate-100 bg-white/70 p-12 text-center shadow-sm">
            <CalendarDays className="mx-auto h-12 w-12 text-slate-300" />
            <p className="mt-4 text-sm text-slate-500">No se encontraron eventos</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="px-4 py-3 font-semibold text-slate-700">Evento</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Fecha</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Estado</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Participantes</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {eventosFiltrados.map((ev) => (
                    <tr
                      key={ev.acontecimiento_id}
                      className="border-b border-slate-50 transition hover:bg-blue-50/30"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {ev.imagen_url ? (
                            <img
                              src={ev.imagen_url}
                              alt=""
                              className="h-10 w-10 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
                              <CalendarDays className="h-4 w-4 text-white" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900">{ev.nombre}</p>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                              {ev.ubicacion?.nombre && (
                                <span className="inline-flex items-center gap-1">
                                  <MapPin className="h-3 w-3" /> {ev.ubicacion.nombre}
                                </span>
                              )}
                              {ev.categoria?.nombre && (
                                <span className="inline-flex items-center gap-1">
                                  <Tag className="h-3 w-3" /> {ev.categoria.nombre}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="flex items-center gap-1">
                          <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
                          <span>
                            {formatFecha(ev.fecha)}
                            {ev.fecha_fin ? ` → ${formatFecha(ev.fecha_fin)}` : ""}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${estadoBadge(
                            ev.estado
                          )}`}
                        >
                          {estadoLabel(ev.estado)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {ev.cupo != null
                          ? `${ev.socios?.length ?? 0} / ${ev.cupo}`
                          : ev.socios?.length ?? 0}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => abrirSocios(ev)}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-indigo-100 hover:text-indigo-700"
                            title="Asignar socios"
                          >
                            <Users className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => abrirEditar(ev)}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-blue-100 hover:text-blue-700"
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEliminar(ev)}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-rose-100 hover:text-rose-700"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal crear/editar */}
        {showForm && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4">
            <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto overscroll-contain rounded-2xl bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">
                  {editingId ? "Editar evento" : "Nuevo evento"}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="sm:col-span-2 text-sm font-medium text-slate-700">
                  Título *
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    placeholder="Ej: Ronda de negocios"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                  />
                </label>

                <label className="sm:col-span-2 text-sm font-medium text-slate-700">
                  Descripción
                  <textarea
                    rows={3}
                    value={form.descripcion}
                    onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                    placeholder="Detalles del evento"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                  />
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Fecha de inicio *
                  <input
                    type="datetime-local"
                    value={form.fecha}
                    onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                  />
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Fecha de fin
                  <input
                    type="datetime-local"
                    value={form.fecha_fin}
                    onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                  />
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Estado
                  <select
                    value={form.estado}
                    onChange={(e) =>
                      setForm({ ...form, estado: e.target.value as EstadoEvento })
                    }
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                  >
                    {ESTADOS.map((e) => (
                      <option key={e.value} value={e.value}>
                        {e.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Cupo (opcional)
                  <input
                    type="number"
                    min={0}
                    value={form.cupo}
                    onChange={(e) => setForm({ ...form, cupo: e.target.value })}
                    placeholder="Sin límite"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                  />
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Ubicación *
                  <input
                    list="evento-ubicaciones"
                    value={form.ubicacion}
                    onChange={(e) => setForm({ ...form, ubicacion: e.target.value })}
                    placeholder="Elegí o escribí una nueva"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                  />
                  <datalist id="evento-ubicaciones">
                    {ubicaciones.map((u) => (
                      <option key={u.ubicacion_id} value={u.nombre} />
                    ))}
                  </datalist>
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Categoría
                  <input
                    list="evento-categorias"
                    value={form.categoria}
                    onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                    placeholder="Elegí o escribí una nueva"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                  />
                  <datalist id="evento-categorias">
                    {categorias.map((c) => (
                      <option key={c.categoria_id} value={c.nombre} />
                    ))}
                  </datalist>
                </label>

                <label className="sm:col-span-2 text-sm font-medium text-slate-700">
                  URL de imagen (opcional)
                  <input
                    type="url"
                    value={form.imagen_url}
                    onChange={(e) => setForm({ ...form, imagen_url: e.target.value })}
                    placeholder="https://..."
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                  />
                </label>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowForm(false)}
                  className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                  disabled={guardando}
                >
                  Cancelar
                </button>
                <button
                  onClick={guardar}
                  disabled={guardando}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
                >
                  {guardando && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingId ? "Guardar cambios" : "Crear evento"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal socios */}
        {selectedEvento && (
          <div className="fixed inset-0 z-[125] flex items-center justify-center bg-black/40 p-4">
            <div className="max-h-[88vh] w-full max-w-2xl overflow-y-auto overscroll-contain rounded-2xl bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">
                  Participantes de "{selectedEvento.nombre}"
                </h2>
                <button
                  onClick={() => {
                    setSelectedEvento(null);
                    setSociosEnEvento([]);
                    setShowAddSocios(false);
                  }}
                  className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              <button
                onClick={() => setShowAddSocios(true)}
                className="mb-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" /> Asignar socios
              </button>

              {showAddSocios && (
                <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Buscar socio por nombre, DNI o CUIT..."
                      value={searchTerm}
                      onChange={(e) => handleSearchSocios(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm"
                    />
                  </div>
                  {searching && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
                  {searchResults.length > 0 && (
                    <div className="max-h-48 space-y-1 overflow-y-auto">
                      {searchResults.map((s) => (
                        <label
                          key={s.socio_id}
                          className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition hover:bg-white"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSocios.includes(Number(s.socio_id))}
                            onChange={() => handleToggleSocio(Number(s.socio_id))}
                            className="h-4 w-4 rounded text-blue-600"
                          />
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {s.socio_nombre}
                            </p>
                            <p className="text-xs text-slate-500">{s.socio_mail}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  {selectedSocios.length > 0 && (
                    <button
                      disabled={asignando}
                      onClick={handleAsignar}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {asignando ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4" />
                      )}
                      Asignar {selectedSocios.length} socio(s)
                    </button>
                  )}
                </div>
              )}

              {loadingSocios ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : sociosEnEvento.length === 0 ? (
                <p className="py-4 text-center text-sm text-slate-500">
                  Todavía no hay socios asignados a este evento
                </p>
              ) : (
                <div className="space-y-2">
                  {sociosEnEvento.map((s) => (
                    <div
                      key={s.socio_id}
                      className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 transition hover:bg-slate-50"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {s.socio_nombre || `Socio #${s.socio_id}`}
                        </p>
                        <p className="text-xs text-slate-500">{s.socio_mail || "—"}</p>
                      </div>
                      <button
                        onClick={() => handleQuitarSocio(s.socio_id)}
                        className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-100 hover:text-rose-700"
                        title="Quitar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
